import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ADS } from '../ads/config';
import { CATALOG, findGame } from '../games/catalog';
import { LEGAL } from './legal/config';
import { Contact } from './legal/LegalPage';
import { setPageMeta } from '../lib/seo';

export const PARENTS_TITLE = 'For Parents and Teachers · SuperFunUBG';
export const PARENTS_DESCRIPTION =
  'What SuperFunUBG is, who runs it and how it keeps kids safe: original kid-friendly games, no accounts, no chat, no purchases and anonymous statistics only.';

/** Games that practice a school skill, with what they practice. */
const LEARNING: Array<[string, string]> = [
  ['math-duel', 'mental math under time pressure'],
  ['speed-typer', 'typing speed and accuracy'],
  ['word-guess', 'spelling and vocabulary'],
  ['sudoku', 'logic and number sense'],
  ['chess', 'planning ahead and strategy'],
  ['element-mixer', 'curiosity and simple science ideas'],
  ['memory-match', 'memory and focus'],
  ['merge-2048', 'powers of two and planning'],
];

/** A plain-language page for adults: who runs the site, what the games are like and what data is (not) collected. */
export function ParentsPage() {
  useEffect(() => {
    setPageMeta(PARENTS_TITLE, PARENTS_DESCRIPTION, '/parents');
  }, []);
  return (
    <main id="main" className="legal">
      <div className="band band--hero legal__hero">
        <div>
          <h1 className="hero__title">For Parents and Teachers</h1>
          <p className="legal__date">What SuperFunUBG is, and how we keep it kid-friendly</p>
        </div>
      </div>
      <article className="legal__body">
        <h2>Who we are</h2>
        <p>
          SuperFunUBG (playsuperfun.com) is a free browser games site made by {LEGAL.operator}, a small company in California. We make every game on the site ourselves:{' '}
          {CATALOG.length} original games with our own names, characters and art. You can reach us any time at <Contact />.
        </p>

        <h2>What the games are like</h2>
        <ul>
          <li>Cartoon action only: foam blasters, bonks, splats and knock-outs. No blood, gore or realistic weapons.</li>
          <li>A few games are gently spooky (such as Night Shift and Forest Nights), with cartoon characters and no gore.</li>
          <li>Opponents are always computer bots or a friend on the same device. There is no online play with strangers.</li>
          <li>No purchases, loot boxes or premium currency. Nothing in any game costs money.</li>
        </ul>

        <h2>Safety and privacy</h2>
        <ul>
          <li>No accounts and no sign-up. We never ask for a name, email or age.</li>
          <li>No chat, messages, comments or player profiles.</li>
          <li>
            Progress, favorites and settings are saved only in your browser, not on our servers. Clearing the site’s data in your browser removes them.
          </li>
          <li>We count visits anonymously (no cookies, no IP addresses kept) to see which games are popular.</li>
          <li>
            {ADS.showAds
              ? 'We show a few ads from Google AdSense, set to child-directed treatment, which turns off personalized ads and ad tracking.'
              : 'The site has no ads right now. If we add ads, they will come from Google AdSense set to child-directed treatment, which turns off personalized ads and ad tracking.'}
          </li>
          <li>
            The “Suggest a Game” form only takes a short idea, and it refuses anything that looks like an email, link or phone number.
          </li>
        </ul>
        <p>
          The full details are in our <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms of Service</Link>.
        </p>

        <h2>How it works</h2>
        <ul>
          <li>Everything runs in the web browser: nothing to download or install.</li>
          <li>Works on Chromebooks, Windows and Mac computers, tablets and phones.</li>
          <li>Each game page explains the controls and gives a few tips, so kids can start without help.</li>
        </ul>

        <h2>For teachers: games that practice skills</h2>
        <p>Some games are a good fit for a brain break or early finishers:</p>
        <ul>
          {LEARNING.map(([id, skill]) => {
            const g = findGame(id);
            return g ? (
              <li key={id}>
                <Link to={g.path}>{g.name}</Link>: {skill}
              </li>
            ) : null;
          })}
        </ul>

        <h2>Questions or concerns</h2>
        <p>
          If something on the site worries you, or a game doesn’t seem right for kids, email <Contact /> and we will look at it quickly.
        </p>
      </article>
      <nav className="legal__nav" aria-label="More">
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/">All games</Link>
      </nav>
    </main>
  );
}
